<template>
  <div class="currency-amount-map">
    <div v-for="(row, index) in rows" :key="row.key" class="currency-amount-map__row d-flex align-items-center mb-1">
      <select
        class="form-select currency-amount-map__currency"
        :disabled="disabled"
        :value="row.currency"
        @change="onCurrencyChange(index, $event)"
      >
        <option
          v-for="currency in options"
          :key="currency"
          :value="currency"
          :disabled="isCurrencyTaken(currency, index)"
        >{{ currency }}</option>
      </select>
      <input
        class="form-control currency-amount-map__amount ms-2"
        type="text"
        inputmode="decimal"
        :disabled="disabled"
        :value="row.amount"
        @input="onAmountInput(index, $event)"
      >
      <button
        v-if="rows.length > 1"
        type="button"
        class="btn btn-outline-secondary btn-sm ms-2"
        :disabled="disabled"
        @click.prevent="removeRow(index)"
      >
        <i class="fa fa-times"></i>
      </button>
      <button
        v-if="index === rows.length - 1"
        type="button"
        class="btn btn-outline-primary btn-sm ms-2"
        :disabled="disabled || !canAddRow"
        @click.prevent="addRow"
      >
        <i class="fa fa-plus"></i>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, PropType, ref, watch} from "vue";
import {useStore} from "vuex";

interface Row {
  key: number
  currency: string
  amount: string
}

const DEFAULT_CURRENCY = 'RUB'

export default defineComponent({
  props: {
    modelValue: {
      type: Object as PropType<Record<string, string> | null>,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  setup(props, {emit}) {
    const store = useStore()
    let keySequence = 0
    const rows = ref<Row[]>([])

    const options = computed<string[]>(() => {
      const fromStore = (store.getters['currencyMethods/currencies'] as string[] | undefined) ?? []
      const all = new Set<string>([DEFAULT_CURRENCY, ...fromStore, ...Object.keys(props.modelValue ?? {})])

      return Array.from(all)
    })

    const toMap = (source: Row[]): Record<string, string> => {
      const result: Record<string, string> = {}
      for (const row of source) {
        const amount = row.amount.replace(/\s/g, '').replace(',', '.')
        if (amount !== '') {
          result[row.currency] = amount
        }
      }

      return result
    }

    const rowsFromMap = (map: Record<string, string> | null | undefined): Row[] => {
      const entries = Object.entries(map ?? {})
      if (entries.length === 0) {
        return [{key: ++keySequence, currency: DEFAULT_CURRENCY, amount: ''}]
      }

      return entries.map(([currency, amount]) => ({key: ++keySequence, currency, amount: String(amount)}))
    }

    const emitRows = () => {
      emit('update:modelValue', toMap(rows.value))
    }

    const isCurrencyTaken = (currency: string, exceptIndex: number): boolean => {
      return rows.value.some((row, index) => index !== exceptIndex && row.currency === currency)
    }

    const canAddRow = computed(() => options.value.some((currency) => !isCurrencyTaken(currency, -1)))

    const addRow = () => {
      const free = options.value.find((currency) => !isCurrencyTaken(currency, -1))
      if (free === undefined) {
        return
      }

      rows.value.push({key: ++keySequence, currency: free, amount: ''})
    }

    const removeRow = (index: number) => {
      rows.value.splice(index, 1)
      emitRows()
    }

    const onCurrencyChange = (index: number, event: Event) => {
      const currency = (event.target as HTMLSelectElement).value
      if (isCurrencyTaken(currency, index)) {
        return
      }

      rows.value[index].currency = currency
      emitRows()
    }

    const onAmountInput = (index: number, event: Event) => {
      rows.value[index].amount = (event.target as HTMLInputElement).value
      emitRows()
    }

    rows.value = rowsFromMap(props.modelValue)

    watch(
      () => props.modelValue,
      (value) => {
        if (JSON.stringify(toMap(rows.value)) !== JSON.stringify(value ?? {})) {
          rows.value = rowsFromMap(value)
        }
      },
      {deep: true}
    )

    onMounted(async () => {
      await store.dispatch('currencyMethods/loadList')
    })

    return {
      rows,
      options,
      canAddRow,
      isCurrencyTaken,
      addRow,
      removeRow,
      onCurrencyChange,
      onAmountInput,
    }
  },
})
</script>

<style scoped>
.currency-amount-map__currency {
  max-width: 110px;
}
</style>
